/**
 * External data sources.
 *
 * A source declares *what* to fetch. A scheduled job does the fetching and
 * writes a normalised result to `data/<id>/latest.json`, which widgets read out
 * of the store like any other vault file.
 *
 * That indirection is what buys the three properties a browser fetch can't
 * have: the tile renders offline because the data is a cached vault file, no
 * API key ever reaches the browser, and the dated snapshots alongside `latest`
 * give history nobody had to design a database for.
 *
 * Normalisation happens in the fetcher, never in the widget. A `quotes` tile
 * doesn't know whether its numbers came from CoinGecko or Finnhub, so swapping
 * providers is a one-line source edit rather than a widget change.
 */

import { z } from 'zod';

// ─── Source declarations (`sources/<id>.md`) ────────────────────────────────

export const SourceBaseSchema = z.object({
  /** Implied by the filename, like widget ids. */
  id: z.string().min(1),
  type: z.string().min(1),
  /** Skip this source without deleting it. */
  enabled: z.boolean().default(true),
});

export const WeatherSourceSchema = SourceBaseSchema.extend({
  type: z.literal('weather'),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
  units: z.enum(['metric', 'imperial']).default('imperial'),
  /** Shown on the tile. Defaults to the coordinates, which is rarely what you want. */
  label: z.string().optional(),
});

export type WeatherSource = z.infer<typeof WeatherSourceSchema>;

export const QuotesSourceSchema = SourceBaseSchema.extend({
  type: z.literal('quotes'),
  /**
   * `coingecko` takes coin ids (`bitcoin`, `ethereum`) and needs no key.
   * `finnhub` takes stock tickers (`AAPL`) and needs `FINNHUB_KEY` in the
   * fetching environment.
   */
  provider: z.enum(['coingecko', 'finnhub']),
  symbols: z.array(z.string().min(1)).min(1).max(25),
  /** Display currency. CoinGecko honours it; Finnhub quotes are always USD. */
  currency: z.string().default('usd'),
});

export type QuotesSource = z.infer<typeof QuotesSourceSchema>;

export const SOURCE_SCHEMAS = {
  weather: WeatherSourceSchema,
  quotes: QuotesSourceSchema,
} as const;

export type SourceType = keyof typeof SOURCE_SCHEMAS;

/** Just enough to route a source config to the right schema. */
export const SourceProbeSchema = z.object({ type: z.string().min(1) });

// ─── Normalised payloads (`data/<id>/latest.json`) ──────────────────────────

/**
 * Every payload carries `fetchedAt` so a tile can say how stale it is. A number
 * that silently stops updating is worse than no number, and with a scheduled
 * fetch the failure mode is exactly that — the file is still there, still
 * parses, and is quietly hours old.
 */
const PayloadBase = { fetchedAt: z.string().min(1) };

export const WeatherDataSchema = z.object({
  ...PayloadBase,
  label: z.string(),
  unit: z.enum(['C', 'F']),
  current: z.object({
    temp: z.number(),
    /** WMO weather code — see {@link weatherDescription}. */
    code: z.number().int(),
    wind: z.number(),
  }),
  today: z.object({ high: z.number(), low: z.number() }),
});

export type WeatherData = z.infer<typeof WeatherDataSchema>;

export const QuoteSchema = z.object({
  symbol: z.string(),
  label: z.string(),
  price: z.number(),
  /** Percent change over 24h. Null when the provider didn't return one. */
  changePercent: z.number().nullable(),
  currency: z.string(),
});

export const QuotesDataSchema = z.object({
  ...PayloadBase,
  quotes: z.array(QuoteSchema),
});

export type Quote = z.infer<typeof QuoteSchema>;
export type QuotesData = z.infer<typeof QuotesDataSchema>;

// ─── WMO weather codes ──────────────────────────────────────────────────────

/**
 * Open-Meteo reports conditions as WMO code 4677, not as text. Grouped rather
 * than exhaustive: "Heavy drizzle" and "Light drizzle" are the same glance.
 */
const WEATHER_CODES: ReadonlyArray<readonly [codes: number[], label: string]> = [
  [[0], 'Clear'],
  [[1], 'Mainly clear'],
  [[2], 'Partly cloudy'],
  [[3], 'Overcast'],
  [[45, 48], 'Fog'],
  [[51, 53, 55], 'Drizzle'],
  [[56, 57], 'Freezing drizzle'],
  [[61, 63, 65], 'Rain'],
  [[66, 67], 'Freezing rain'],
  [[71, 73, 75], 'Snow'],
  [[77], 'Snow grains'],
  [[80, 81, 82], 'Rain showers'],
  [[85, 86], 'Snow showers'],
  [[95], 'Thunderstorm'],
  [[96, 99], 'Thunderstorm, hail'],
];

export function weatherDescription(code: number): string {
  return WEATHER_CODES.find(([codes]) => codes.includes(code))?.[1] ?? 'Unknown';
}
